import { useState, useEffect, useCallback } from 'react';
import { X, Search, ChevronsUpDown, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { StudentInfo } from './types';

interface StudentSearchMultiProps {
  selectedStudents: StudentInfo[];
  onChange: (students: StudentInfo[]) => void;
}

export function StudentSearchMulti({ selectedStudents, onChange }: StudentSearchMultiProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<StudentInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const searchStudents = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('v_fishsix_students_all')
        .select('code, first_name, last_name, nickname, hours')
        .or(`code.ilike.%${query}%,first_name.ilike.%${query}%,nickname.ilike.%${query}%`)
        .is('deleted_at', null)
        .limit(20);

      if (error) {
        console.error('Error searching students:', error);
        setSearchResults([]);
        return;
      }

      setSearchResults(data || []);
    } catch (e) {
      console.error('Error in student search:', e);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchStudents(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchStudents]);

  const handleSelect = (student: StudentInfo) => {
    const isSelected = selectedStudents.some(s => s.code === student.code);
    if (isSelected) {
      onChange(selectedStudents.filter(s => s.code !== student.code));
    } else {
      onChange([...selectedStudents, student]);
    }
  };

  const handleRemove = (code: string) => {
    onChange(selectedStudents.filter(s => s.code !== code));
  };

  return (
    <div className="space-y-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            <span className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              ค้นหานักเรียน...
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="ค้นหาด้วยรหัส, ชื่อ หรือชื่อเล่น..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isLoading && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">กำลังค้นหา...</span>
                </div>
              )}
              {!isLoading && searchQuery.length < 2 && (
                <CommandEmpty>พิมพ์อย่างน้อย 2 ตัวอักษร</CommandEmpty>
              )}
              {!isLoading && searchQuery.length >= 2 && searchResults.length === 0 && (
                <CommandEmpty>ไม่พบนักเรียน</CommandEmpty>
              )}
              {!isLoading && searchResults.length > 0 && (
                <CommandGroup>
                  {searchResults.map((student) => {
                    const isSelected = selectedStudents.some(s => s.code === student.code);
                    return (
                      <CommandItem
                        key={student.code}
                        value={student.code}
                        onSelect={() => handleSelect(student)}
                        className="flex items-center justify-between"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {student.code} - {student.nickname || student.first_name}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {student.first_name} {student.last_name} | ชม.คงเหลือ: {student.hours ?? 0}
                          </span>
                        </div>
                        <Check
                          className={cn(
                            'h-4 w-4',
                            isSelected ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedStudents.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedStudents.map((student) => (
            <Badge
              key={student.code}
              variant="secondary"
              className="flex items-center gap-1 py-1 px-2"
            >
              <span>{student.code} - {student.nickname || student.first_name}</span>
              <button
                type="button"
                onClick={() => handleRemove(student.code)}
                className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {selectedStudents.length > 0 && (
        <p className="text-sm text-muted-foreground">
          เลือกแล้ว {selectedStudents.length} คน
        </p>
      )}
    </div>
  );
}
